<template>
    <div class="grid-content">
        <div class="grid-item grid-item-full">
            <div class="field" :class="{'is-error': hasError}">
                <label class="field-label stack-s">HTML Content</label>
                <div style="position:absolute;right:10px;top:0;">
                    <slot name="buttons" :do-merge="v => mergeTag(v)"/>
                    <r-button
                        type="link"
                        class="inline-s"
                        :disabled="!canAddLang"
                        @click="openLangModal">Add Language
                    </r-button>
                    <r-button
                        type="link"
                        @click="removeActiveLang">Remove Language ({{activeLang}})
                    </r-button>
                </div>
                <div class="editor" :class="{'is-active': focused}">
                    <div class="editor-menu">
                        <div class="grid-fitted align-items-center">
                            <div class="grid-item">
                                <ul class="tab wrap-items">
                                    <li class="tab-item" v-for="choice in choices" :key="choice.langIso">
                                        <a class="tab-link"
                                           @click="activateLang(choice)"
                                           :class="{'is-active': isActiveTab(choice), 'has-error': hasError}">{{getLangName(choice)}}</a>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    <div class="editor-textarea-wrapper">
                <textarea
                    v-fs-block
                    ref="textarea"
                    class="editor-textarea"
                    rows="20"
                    @focusin="focused = true"
                    @focusout="focused = false"
                    v-model="content"></textarea>
                    </div>
                </div>
                <v-modal
                    v-if="showAddLangModal && canAddLang"
                    :scroll="true"
                    title="Add Language"
                    cancelLabel="Close"
                    @close="closeLangModal">
                    <template #content>
                        <div>
                            <v-select
                                label="Select new language"
                                v-model="newLang"
                                preselect-first
                                option-label="name"
                                option-key="langIso"
                                placeholder="Select language"
                                :options="languageChoices"/>
                        </div>
                    </template>
                    <template #actions>
                        <r-button
                            type="primary"
                            @click="addLang">Add Language
                        </r-button>
                    </template>
                </v-modal>
                <v-modal
                    v-if="showPreviewModal"
                    size="extra-large"
                    :scroll="true"
                    title="Page Preview"
                    cancelLabel="Close"
                    @close="closePreviewModal">
                    <template #content>
                        <div>
                            <v-iframe-sandbox :html="previewHtml"/>
                        </div>
                    </template>
                </v-modal>
            </div>
        </div>
        <div class="grid-item grid-item-full text-right">
            <r-button :disabled="!canPreview" @click="openPreviewModal">Preview Page</r-button>
        </div>
    </div>
</template>

<script>
    import LANGUAGES from '@/data/languages';
    import {vSelect} from '@/components/form';
    import vModal from '@/components/modal/v-modal';
    import {DisplayChoiceModel} from '@/common/models/rules/actions/display-other-choices-action-model';
    import {createDialog, vConfirm} from '@/components/modal/dialogs';
    import vIframeSandbox from '@/components/v-iframe-sandbox';
    import _ from '@/common/helpers';

    export default {
        name: 'choices-editor',
        components: {
            vModal,
            vSelect,
            vIframeSandbox,
        },
        props: {
            /**
             * @type {Array}
             */
            choices: Array,
            validate: Object,
        },
        computed: {
            activeChoice() {
                const target = this.activeLang || this.choices[0].langIso;
                return this.choices.find(v => v.langIso === target);
            },
            content: {
                get() {
                    return this.activeChoice.content;
                },
                set(content) {
                    this.activeChoice.content = content;
                    this.updateChoices();
                },
            },
            hasError() {
                return this.validate.$error;
            },
            languageChoices() {
                const currentlySelected = this.choices.map(v => v.langIso);
                return [...LANGUAGES]
                    .filter(v => !currentlySelected.includes(v.langIso))
                    .sort(({name}, {name: nameB}) => (name > nameB ? 1 : -1));
            },
            canAddLang() {
                return this.languageChoices.length > 0;
            },
            canPreview() {
                return !_.isBlank(this.activeChoice.content);
            },
            previewHtml() {
                return `<html><body>${this.activeChoice.content}</body></html>`;
            },
        },
        data() {
            return {
                focused: false,
                activeLang: this.choices[0].langIso,
                showAddLangModal: false,
                showPreviewModal: false,
                newLang: null,
            };
        },
        methods: {
            activateLang({langIso}) {
                this.activeLang = langIso;
            },
            isActiveTab({langIso}) {
                return this.activeLang === langIso;
            },
            async removeActiveLang() {
                const confirmDialog = createDialog(vConfirm, this);
                const dialogParams = {
                    title: `Remove ${this.getLangName({langIso: this.activeLang})}`,
                    content: `Are you sure you want to remove this choice?`,
                    confirmLabel: 'Delete',
                    dangerConfirm: true,
                };
                if (await confirmDialog(dialogParams)) {
                    const index = this.choices.findIndex(v => v.langIso === this.activeLang);
                    const newActiveIndex = index - 1 >= 0 ? index - 1 : 0;
                    this.activeLang = this.choices[newActiveIndex].langIso;
                    this.choices.splice(index, 1);
                    this.updateChoices();
                }
            },
            openLangModal() {
                this.showAddLangModal = true;
            },
            closeLangModal() {
                this.showAddLangModal = false;
            },
            openPreviewModal() {
                this.showPreviewModal = true;
            },
            closePreviewModal() {
                this.showPreviewModal = false;
            },
            addLang() {
                this.choices.push(new DisplayChoiceModel({langIso: this.newLang}));
                const [firstItem] = this.languageChoices;
                this.newLang = firstItem.langIso;
                this.closeLangModal();
                this.updateChoices();
            },
            updateChoices() {
                this.$emit('input', this.choices);
            },
            getLangName({langIso}) {
                const result = LANGUAGES.find(v => v.langIso === langIso);
                if (result) {
                    return result.name;
                }
                return '?';
            },
            /**
             * Use the provided string content and insert it into the textarea either at
             * the position of the cursor or by replacing an existing text selection within.
             * @param {string} content
             */
            mergeTag({content}) {
                const currentContent = this.content || '';
                const {selectionStart = 0, selectionEnd = 0} = this.$refs.textarea;
                const before = currentContent.substring(0, selectionStart);
                const after = currentContent.substring(selectionEnd);
                this.content = `${before}${content}${after}`;
                this.$nextTick(() => {
                    this.$refs.textarea.focus();
                    this.$refs.textarea
                        .setSelectionRange(selectionStart, selectionStart + content.length);
                });
            },
        },
    };
</script>

<style scoped>

</style>
